/**
 * Research-derived time budget system for survey and interview generation.
 *
 * Statistical benchmarks sourced from quantitative UX research on survey and
 * qualitative interview completion times:
 *
 * Survey:
 *   - Initialization / start-up buffer: 15–30 s  (midpoint 20 s)
 *   - Single-choice / rating scale: 5–10 s        (midpoint 7 s)
 *   - Complex multiple-choice: 15–30 s            (midpoint 20 s)
 *   - Short open-ended text: 30–90 s              (midpoint 60 s)
 *   - Optimal target duration: 5–10 min
 *   Typical distribution in our surveys: ~30% open, ~30% scale/yes_no, ~40% mc/checkbox
 *   Weighted average per question: 0.30×60 + 0.30×7 + 0.40×20 ≈ 28 s
 *
 * Qualitative Interview:
 *   - Optimal total duration: 45–90 min
 *   - Structural buffer (intros, background, closing): 20–30 min
 *   - Primary question count: 6–12 main questions
 *   - Per-question block (response + follow-up discussion + probes): ~5–8 min avg
 */
// ─── Per-question-type time costs (seconds) ─────────────────────────────────
export const SURVEY_QUESTION_SECS = {
    yes_no: 7,
    scale: 7,
    multiple_choice: 20,
    checkbox: 20,
    open: 60,
};
/** Weighted average assuming ~30% open, ~30% quick, ~40% structured */
export const SURVEY_AVG_QUESTION_SECS = 28;
/** One-time startup buffer deducted from the total survey budget */
export const SURVEY_STARTUP_BUFFER_SECS = 20;
// ─── Selection cap for holistic script generation ───────────────────────────
/**
 * Upper bound on the number of mindmap nodes that can feed a single survey or
 * interview script. The holistic generator sends every selected node into one
 * LLM call; the cap keeps prompt size predictable and respondent surveys focused.
 */
export const MAX_SELECTED_NODES_PER_SCRIPT = 30;
/**
 * Survey duration presets.
 * Total questions = floor((minutes×60 − 20) / 28):
 *   5 min → 10, 8 min → 16 → capped, 12 min → 25 → capped
 * Capped to prevent bloated surveys that exceed respondent fatigue thresholds.
 */
export const SURVEY_DURATION_PRESETS = [
    { label: 'Quick', minutes: 5, description: '~5 min', totalQuestions: 10 },
    { label: 'Standard', minutes: 8, description: '~8 min', totalQuestions: 14 },
    { label: 'Thorough', minutes: 12, description: '~12 min', totalQuestions: 20 },
];
/**
 * Interview duration presets.
 * Derived from primary question count (6–12) across optimal 45–90 min range.
 * Shorter interviews use a proportionally smaller structural buffer.
 */
export const INTERVIEW_DURATION_PRESETS = [
    { label: 'Quick', minutes: 30, description: '~30 min', totalQuestions: 5 },
    { label: 'Standard', minutes: 45, description: '~45 min', totalQuestions: 8 },
    { label: 'Thorough', minutes: 60, description: '~60 min', totalQuestions: 12 },
];
// ─── Helpers ─────────────────────────────────────────────────────────────────
/** Return the presets array for the given method. */
export function getDurationPresets(method) {
    return method === 'survey' ? SURVEY_DURATION_PRESETS : INTERVIEW_DURATION_PRESETS;
}
/** Return the Standard (default) preset for a method. */
export function getDefaultPreset(method) {
    const presets = getDurationPresets(method);
    return presets.find((p) => p.label === 'Standard') ?? presets[1];
}
/** Return the preset matching `minutes`, or the Standard preset if not found. */
export function getPresetByMinutes(method, minutes) {
    return getDurationPresets(method).find((p) => p.minutes === minutes) ?? getDefaultPreset(method);
}
/**
 * Return the total question budget for a given target duration.
 * Looks up the matching preset; falls back to Standard if `minutes` is not a
 * recognised preset value (e.g. an arbitrary API caller value).
 */
export function getTotalQuestions(method, minutes) {
    return getPresetByMinutes(method, minutes).totalQuestions;
}
